import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import type { ColumnDef } from '../common/tableFeatures';
import { selectColumn } from '../common/tableCells';
import { StatusChip } from '../common/StatusChip';
import type { Device } from '../../types/api';
import { calculateMacBasedSerial } from '../../utils/deviceSerial';

interface CreateDeviceColumnsOptions {
  /** Navigate to a device detail page. */
  onOpenDevice: (deviceId: string) => void;
  /** Open the row actions menu anchored to the clicked button. */
  onOpenActions: (event: React.MouseEvent<HTMLElement>, device: Device) => void;
}

/** Compact "time ago" so the Last Seen column stays narrow; the full timestamp
 *  is still available on hover. */
const formatLastSeen = (date: Date): string => {
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h ago`;
  if (minutes < 43200) return `${Math.round(minutes / 1440)}d ago`;
  return date.toLocaleDateString();
};

/**
 * Build the TanStack column definitions for the device list. Kept as a factory
 * so the cell renderers can close over navigation / menu callbacks while the
 * result stays memoisable in the parent.
 */
export const createDeviceColumns = ({
  onOpenDevice,
  onOpenActions,
}: CreateDeviceColumnsOptions): ColumnDef<Device>[] => [
  selectColumn<Device>((row) => row.name, 'Select all devices'),
  {
    accessorKey: 'name',
    header: 'Device Name',
    cell: ({ row }) => (
      <Typography
        variant="body2"
        noWrap
        sx={{
          fontWeight: 500,
          color: 'primary.main',
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' },
        }}
        onClick={() => onOpenDevice(row.original.deviceId)}
      >
        {row.getValue('name')}
      </Typography>
    ),
    size: 200,
  },
  {
    accessorKey: 'customerName',
    header: 'Customer',
    cell: ({ row }) => row.getValue('customerName') || '-',
  },
  {
    accessorKey: 'location',
    header: 'Location',
    cell: ({ row }) => row.getValue('location') || '-',
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue('status') as Device['status'];
      return <StatusChip status={status} type="device" />;
    },
  },
  {
    accessorKey: 'registrationStatus',
    header: 'Registration',
    cell: ({ row }) => {
      const regStatus = row.getValue('registrationStatus') as Device['registrationStatus'];
      if (!regStatus) return null;
      return <StatusChip status={regStatus} type="registration" />;
    },
  },
  {
    accessorKey: 'registrationId',
    header: 'Registration ID',
  },
  {
    accessorKey: 'macAddress',
    header: 'MAC Address',
    cell: ({ row }) => row.getValue('macAddress') || '-',
  },
  {
    id: 'macBasedSerial',
    header: 'MAC-based Serial',
    cell: ({ row }) => {
      const macAddress = row.getValue('macAddress') as string | undefined;
      return calculateMacBasedSerial(macAddress) || '-';
    },
  },
  {
    accessorKey: 'secureSerial',
    header: 'Secure Serial',
    cell: ({ row }) => row.getValue('secureSerial') || '-',
  },
  {
    accessorKey: 'secureCode',
    header: 'Secure Code',
    cell: ({ row }) => row.getValue('secureCode') || '-',
  },
  {
    accessorKey: 'groupName',
    header: 'Group',
    cell: ({ row }) => row.getValue('groupName') || '-',
  },
  {
    // Explicit id: TanStack would otherwise derive `hardware_model` from the
    // dotted accessorKey, which never matches the dotted id the column
    // selector uses - leaving the column permanently visible.
    id: 'hardware.model',
    accessorKey: 'hardware.model',
    header: 'Model',
    cell: ({ row }) => row.original.hardware?.model || '-',
  },
  {
    accessorKey: 'clientVersion',
    header: 'Client Version',
    cell: ({ row }) => row.original.clientVersion || '-',
  },
  {
    accessorKey: 'comnovoGatewayVersion',
    header: 'Comnovo Version',
    cell: ({ row }) => row.original.comnovoGatewayVersion || 'Unknown',
    size: 120,
  },
  {
    accessorKey: 'dpsEnvironment',
    header: 'DPS Environment',
    cell: ({ row }) => {
      const dpsEnv = row.original.dpsEnvironment;
      const dpsIdScope = row.original.dpsIdScope;

      if (!dpsEnv && !dpsIdScope) return '-';

      return (
        <Tooltip title={`DpsIdScope: ${dpsIdScope || 'N/A'}`}>
          <Box component="span">
            <StatusChip status={dpsEnv || 'Unknown'} type="dpsEnv" />
          </Box>
        </Tooltip>
      );
    },
    size: 130,
  },
  {
    accessorKey: 'dpsEnrollmentStatus',
    header: 'DPS Enrollment',
    cell: ({ row }) => {
      const enrollmentStatus = row.original.dpsEnrollmentStatus;
      const assignedHub = row.original.dpsAssignedHub;
      const enrolledAt = row.original.dpsEnrolledAt;
      const lastError = row.original.dpsLastError;

      if (!enrollmentStatus) {
        return <StatusChip status="not_enrolled" type="dps" />;
      }

      const tooltipContent = (
        <Box>
          <Typography variant="caption" sx={{
            display: 'block'
          }}>
            Status: {enrollmentStatus}
          </Typography>
          {assignedHub && (
            <Typography variant="caption" sx={{
              display: 'block'
            }}>
              Hub: {assignedHub}
            </Typography>
          )}
          {enrolledAt && (
            <Typography variant="caption" sx={{
              display: 'block'
            }}>
              Enrolled: {new Date(enrolledAt).toLocaleString()}
            </Typography>
          )}
          {lastError && (
            <Typography variant="caption" color="error" sx={{
              display: 'block'
            }}>
              Error: {lastError}
            </Typography>
          )}
        </Box>
      );

      return (
        <Tooltip title={tooltipContent}>
          <Box component="span">
            <StatusChip status={enrollmentStatus} type="dps" />
          </Box>
        </Tooltip>
      );
    },
    size: 140,
  },
  {
    id: 'gsmStatus',
    accessorKey: 'gsmDisabled',
    header: 'GSM Modem',
    cell: ({ row }) => {
      const gsmDisabled = row.original.gsmDisabled;

      // Null coalescing handles both undefined and null (unknown state).
      if (gsmDisabled == null) {
        return <StatusChip status="unknown" type="gsm" />;
      }

      const status = gsmDisabled ? 'disabled' : 'enabled';
      return <StatusChip status={status} type="gsm" />;
    },
    size: 150,
  },
  {
    accessorKey: 'totalDiskGb',
    header: 'Disk Capacity',
    cell: ({ row }) => {
      const totalDisk = row.original.totalDiskGb;
      return totalDisk ? `${totalDisk.toFixed(1)} GB` : '-';
    },
    size: 120,
  },
  {
    // Explicit id, for the same reason as `hardware.model` above.
    id: 'network.ipAddress',
    accessorKey: 'network.ipAddress',
    header: 'IP Address',
    cell: ({ row }) => row.original.network?.ipAddress || '-',
  },
  {
    accessorKey: 'lastSeen',
    header: 'Last Seen',
    cell: ({ row }) => {
      const lastSeen = row.getValue('lastSeen') as string;
      if (!lastSeen) return '-';
      const date = new Date(lastSeen);
      return (
        <Tooltip title={date.toLocaleString()}>
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {formatLastSeen(date)}
          </Box>
        </Tooltip>
      );
    },
    size: 110,
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <IconButton
        size="small"
        sx={{ p: 0.5 }}
        onClick={(e) => onOpenActions(e, row.original)}
        aria-label={`More actions for ${row.original.name}`}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
    ),
    size: 56,
    meta: { sticky: 'right' },
  },
];
